import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomPagination from "../../components/CustomPagination";
import { get_ledger_data } from "../../reduxData/user/userAction";
import EmptyData from "../../components/EmptyData";
import LedgerDetailSidebar from "../../components/LedgerDetailSidebar";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../hooks/formatDateTime";
import { formatPrice } from "../../hooks/formatPrice";

const LedgerListing = () => {
    const { i18n } = useTranslation();
    const dispatch = useDispatch();
    const [resetPage, setResetPage] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedPayoutType, setSelectedPayoutType] = useState("");
    const [selectedEventType, setSelectedEventType] = useState("");
    const [selectedItem, setSelectedItem] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const { user } = useSelector((state) => state.auth);
    const { ledgerData, totalLedger } = useSelector((state) => state.user);
    const ledgerList = ledgerData?.data || [];

    const searchTimeoutRef = useRef(null);

    const fetchLedgerData = async (page = 1, perPage = 10, searchValue = search, eventType = selectedEventType, payoutType = selectedPayoutType) => {
        const payload = {
            search: searchValue,
            event_type: eventType,
            payout_type: payoutType,
        };
        await get_ledger_data(dispatch, page, perPage, payload);
    };

    useEffect(() => {
        // console.log("user?.token", user);
        // if (!user?.token) return;
        fetchLedgerData(1, 10);
    }, [user]);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, []);

    const handleSearchChange = (value) => {
        setSearch(value);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setResetPage((prev) => !prev);
            fetchLedgerData(1, 10, value, selectedEventType, selectedPayoutType);
        }, 400);
    };

    const handleSearchEnter = async () => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        setResetPage((prev) => !prev);
        await fetchLedgerData(1, 10, search, selectedEventType, selectedPayoutType);
    };

    const handleEventTypeChange = async (value) => {
        setSelectedEventType(value);
        setResetPage((prev) => !prev);
        await fetchLedgerData(1, 10, search, value, selectedPayoutType);
    };

    const handlePayoutTypeChange = async (value) => {
        setSelectedPayoutType(value);
        setResetPage((prev) => !prev);
        await fetchLedgerData(1, 10, search, selectedEventType, value);
    };

    const hasActiveFilters = Boolean(search || selectedEventType || selectedPayoutType);

    const handleClearFilter = async () => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        setSearch("");
        setSelectedEventType("");
        setSelectedPayoutType("");
        setResetPage((prev) => !prev);
        await fetchLedgerData(1, 10, "", "", "");
    };

    const handleViewDetail = (item) => {
        setSelectedItem(item);
        setIsSidebarOpen(true);
    };

    const handleCloseSidebar = () => {
        setIsSidebarOpen(false);
    };

    const getDirectionClass = (direction) => {
        const dir = direction?.toLowerCase();
        if (dir === "credit") return "ledger-badge credit";
        if (dir === "debit") return "ledger-badge debit";
        return "ledger-badge";
    };

    const payoutTypes = useMemo(() => {
        const types =
            ledgerList
                ?.map((item) => item?.direction)
                ?.filter(Boolean) || [];

        return [...new Set(types)];
    }, [ledgerList]);


    const EventTypes = useMemo(() => {
        const types = ledgerList?.map((item) => item?.event_type)?.filter(Boolean) || [];
        return [...new Set(types)];
    }, [ledgerList]);

    return (
        <div className="ledger-page container">
            <div className="ledger-header">
                <h2 className="ledger-title">
                    Ledger
                </h2>
            </div>

            <div className="ledger-stats-grid">
                <div className="ledger-stat-card balance">
                    <div className="ledger-stat-icon">€</div>
                    <div>
                        <p className="ledger-stat-label">Current Balance</p>
                        <h3 className="ledger-stat-value">
                            {formatPrice(ledgerData?.balance?.current_balance, i18n, true, true)}
                        </h3>
                        <span className="ledger-stat-sub">Live account balance</span>
                    </div>
                </div>

                <div className="ledger-stat-card credit">
                    <div className="ledger-stat-icon">↑</div>
                    <div>
                        <p className="ledger-stat-label">Total Credits</p>
                        <h3 className="ledger-stat-value success-text">
                            {formatPrice(ledgerData?.balance?.positive_balance, i18n)}
                        </h3>
                        <span className="ledger-stat-sub">Incoming funds</span>
                    </div>
                </div>

                <div className="ledger-stat-card debit">
                    <div className="ledger-stat-icon">↓</div>
                    <div>
                        <p className="ledger-stat-label">Total Debits</p>
                        <h3 className="ledger-stat-value debit-amount">
                            {formatPrice(ledgerData?.balance?.negative_balance, i18n)}
                        </h3>
                        <span className="ledger-stat-sub">Outgoing funds</span>
                    </div>
                </div>

                <div className="ledger-stat-card entries">
                    <div className="ledger-stat-icon">#</div>
                    <div>
                        <p className="ledger-stat-label">Total Entries</p>
                        <h3 className="ledger-stat-value">{totalLedger || ledgerList?.length || 0}</h3>
                        <span className="ledger-stat-sub">Ledger records</span>
                    </div>
                </div>
            </div>

            <div className="ledger-filter-wrapper mb-4">
                <div className="row g-3 align-items-end">
                    <div className="col-md-4">
                        <label className="form-label">Search</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by order id"
                            value={search}
                            onChange={(e) =>
                                handleSearchChange(e.target.value)
                            }
                            onKeyDown={async (e) => {
                                if (e.key === "Enter") {
                                    await handleSearchEnter();
                                }
                            }}
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label">
                            Event Type
                        </label>
                        <select
                            className="form-select"
                            value={selectedEventType}
                            onChange={(e) =>
                                handleEventTypeChange(e.target.value)
                            }
                        >
                            <option value="">
                                All
                            </option>

                            {EventTypes.map((type, index) => (
                                <option key={index} value={type}>
                                    {type?.replace(/_/g, " ")?.replace(/\b\w/g, (char) => char.toUpperCase())}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label">
                            Payout Type
                        </label>
                        <select
                            className="form-select"
                            value={selectedPayoutType}
                            onChange={(e) =>
                                handlePayoutTypeChange(e.target.value)
                            }>
                            <option value="">All</option>
                            {payoutTypes.map((type, index) => (
                                <option key={index} value={type}>
                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-2">
                        <button
                            type="button"
                            className="btn audit-clear-btn"
                            onClick={handleClearFilter}
                            disabled={!hasActiveFilters}
                        >Clear Filters</button>
                    </div>
                </div>
            </div>
            <div className="table-responsive">
                <table className="table user-management-table table-hover">
                    <thead className="border-gray">
                        <tr>
                            <th>Date</th>
                            <th>Seller Name</th>
                            <th>Order ID</th>
                            <th>Event</th>
                            <th className="text-center">Dir</th>
                            <th className="text-end">Amount</th>
                            <th className="text-end">Total Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {totalLedger === 0 && (
                            <tr>
                                <td colSpan="7">
                                    <EmptyData />
                                </td>
                            </tr>
                        )}
                        {ledgerList.map((item) => (
                                <tr
                                    key={item.id}
                                    className={`ledger-row-clickable ${selectedItem?.id === item.id && isSidebarOpen ? "selected" : ""}`}
                                    onClick={() => handleViewDetail(item)}
                                >
                                    <td>
                                        {formatDateTime(item.created_at, i18n)}
                                    </td>
                                    <td>{item.seller_name || "-"}</td>
                                    <td>{item.order_id || "-"}</td>
                                    <td className="text-capitalize">
                                        {item.event_type?.replace(/_/g, " ")?.replace(/\b\w/g, (char) => char.toUpperCase())}
                                    </td>
                                    <td className="text-center">
                                        <span
                                            className={getDirectionClass(
                                                item.direction
                                            )}>
                                            {item.direction?.toLowerCase()}
                                        </span>
                                    </td>
                                    <td className="text-end">
                                        <div
                                            className={`ledger-amount ${item.direction?.toLowerCase() ===
                                                "credit"
                                                ? "credit-text"
                                                : "debit-text"
                                                }`}
                                        >
                                            {formatPrice(item.amount, i18n)}
                                        </div>
                                    </td>
                                    <td className="text-end">
                                        <div className="ledger-balance">
                                            {formatPrice(item.total_balance, i18n)}
                                        </div>
                                    </td>
                                </tr>
                            ))}

                    </tbody>
                </table>
            </div>
            {ledgerList.length> 0 && (
                <div className="mt-4">
                    <CustomPagination
                        total={totalLedger || 10}
                        resetPage={resetPage}
                        onPageChange={async (page, perPage) =>
                            await fetchLedgerData(
                                page,
                                perPage,
                                search
                            )
                        }
                    />
                </div>
            )}

            <LedgerDetailSidebar
                isOpen={isSidebarOpen}
                item={selectedItem}
                onClose={handleCloseSidebar}
            />
        </div>
    );
};

export default LedgerListing;
